<template>
    <b-modal
        id="rubric-survey-user-peers"
        v-model="modal"
        title="Peers"
        modal-class="apprentice-default-modal"
        @close="onClose"
        size="lg"
        ok-only
        :ok-title="$t('common.close')">
        <design-2021-apprentice-data-table
            class="students-peers-table"
            v-if="rubricSurveyUser"
            :items="rubricSurveyUser.peers"
            :fields="fields"
            nosearch
            :show-empty="true">
            <template v-slot:options>
                <design-2021-apprentice-select-search-component
                    class="students-peers-select"
                    :selected-in="selectedUser"
                    label="full_name"
                    :options="availableUsers"
                    :placeholder="$t('rubrics.choose_a_student')"
                    :trackBy="'full_name'"
                    @input="updated">
                </design-2021-apprentice-select-search-component>

                <b-button
                    class="btn-add btn-add-survey-user-peer"
                    variant="primary"
                    @click="addPeer"
                    :disabled="!selectedUser">
                    {{ $t('rubrics.add_peer') }}
                </b-button>
            </template>

            <template v-slot:cell(submitted)="data">
                <div class="status">
                    <i
                        class="fas fa-fw fa-check-circle text-success"
                        v-if="data.item.rubric_survey_user.rubric_forms[0].hasResultForUser(data.item.user_id)"></i>
                    <i
                        class="fas fa-fw fa-times-circle text-danger"
                        v-else></i>
                </div>
            </template>

            <template v-slot:cell(date)="data">
                {{ data.item.created_at | date_time_om_year }}
            </template>

            <template v-slot:cell(action)="data">
                <div class="group-actions">
                    <b-button
                        class="btn-open btn-survey-peer-open"
                        variant="primary-icon-only"
                        :v-b-tooltip="$t('common.open')"
                        :href="routes.rubricSurveyUserShow(data.item.rubric_survey_user_id)">
                    </b-button>
                    <b-button
                        class="btn-remove btn-survey-peer-remove"
                        variant="danger-icon-only"
                        :v-b-tooltip="$t('common.delete')"
                        @click="removePeer(data.item)">
                    </b-button>
                </div>
            </template>

            <template #empty="scope">
                <div class="not-found with-header empty-student-peers-list">
                    <div class="icon">
                        <i class="fas fa-exclamation-circle"></i>
                    </div>
                    <div class="alert-notice">{{ $t('rubrics.no_students_linked') }}</div>
                </div>
            </template>
        </design-2021-apprentice-data-table>
    </b-modal>
</template>

<script>
import { ords } from '../../../../../app';

export default {
    name: 'design-2021-rubric-survey-show-peers-modal-component',
    data() {
        return {
            rubricSurveyUser: null,
            modal: false,
            selectedUser: null,
            subscription: null,
            users: [],
            users_loaded: false,
            fields: [
                {
                    key: 'user.full_name',
                    label: this.$t('rubrics.student'),
                    sortable: true,
                },
                {
                    key: 'submitted',
                    label: this.$t('rubrics.no_students_linked'),
                    sortable: true,
                },
                {
                    key: 'date',
                    label: this.$t('rubrics.date'),
                    sortable: true,
                },
                {
                    key: 'action',
                    label: this.$t('common.actions'),
                },
            ],
        };
    },
    computed: {
        availableUsers() {
            if (!this.rubricSurveyUser) {
                return this.users;
            }

            const peer_ids = this.rubricSurveyUser.peers.map((peer) => peer.user_id);
            return this.users.filter(
                (user) => !peer_ids.includes(user.id) && user.id !== this.rubricSurveyUser.user_id,
            );
        },
    },
    methods: {
        addPeer() {
            if (!this.selectedUser) {
                return;
            }

            const data = new FormData();
            const headers = { 'Content-Type': 'multipart/form-data' };
            data.append('rubric_user_id', this.rubricSurveyUser.id);
            data.append('rubric_type', 'rubricSurvey');
            data.append('user_id[0]', this.selectedUser.id);

            this.alert.saving();
            this.axios.post(this.routes.rubricSurveyUserPeerStore(), data, { headers: headers }).then(
                (response) => {
                    this.rubricSurveyUser.addRelation('peers', response.data);
                    this.selectedUser = null;
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        removePeer(peer) {
            this.alert
                .confirm(this.$t('rubrics.delete_peer'), this.$t('rubrics.are_you_sure_you_want_to_delete_this_peer'))
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.alert.saving();
                    this.axios.delete(this.routes.rubricSurveyUserPeerRemove(peer.id)).then(
                        (response) => {
                            peer.remove();
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                });
        },
        show(rubric_survey_user_id) {
            if (!this.users_loaded) {
                this.axios.get(this.routes.schoolGetUsers() + '?type=student').then(
                    (response) => {
                        this.users = response.data;
                        this.users_loaded = true;
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            }

            this.subscription = ords
                .use('rubricSurveyUser')
                .with('peers', (peerCallback) => {
                    peerCallback.whereHas('user', (userCallback) => {
                        userCallback.where('type', '=', 'student');
                    });
                    peerCallback.with('user');
                    peerCallback.with('rubric_survey_user', (rubricSurveyUserCallback) => {
                        rubricSurveyUserCallback.with('rubric_forms', (rubricFormCallback) => {
                            rubricFormCallback.with('rubric_form_results');
                        });
                    });
                    peerCallback.where('rubric_survey_user_id', '=', rubric_survey_user_id); // todo should be where has but ords needs fixing
                })
                .find(rubric_survey_user_id)
                .subscribe((rubricSurveyUser) => {
                    this.rubricSurveyUser = rubricSurveyUser;
                });
            this.modal = true;
        },
        onClose() {
            this.subscription.unsubscribe();
        },

        updated(checked) {
            if (checked) {
                this.selectedUser = checked;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.group-actions {
    display: flex;
    align-items: center;

    .btn-survey-peer-open {
        margin-right: map-get($grid-gutter-spacing, 'twenty-one');
    }
}

.students-peers-table ::v-deep {
    .apprentice-data-table-actions {
        > button,
        > div {
            &:not(:last-child) {
                margin-right: map-get($grid-gutter-spacing, 'fifteen');
            }
        }
    }
}

.status {
    i {
        font-size: 1.3125rem;
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .students-peers-select {
        width: 200px;
    }

    .students-peers-table ::v-deep {
        .apprentice-data-table-actions {
            justify-content: flex-end;
        }
    }
}
</style>
